// src/components/ecommerce/ProductModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, 
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Star,
  Heart,
  Share2,
  ShoppingCart,
  Package,
  Truck,
  Shield,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus,
  Minus,
  ExternalLink,
  MessageCircle,
  ThumbsUp,
  ThumbsDown,
  Flag,
  User,
  Camera,
  Play,
  Download,
  Info,
  Tag,
  MapPin,
  Calendar
} from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { ecommerceService } from '../../services/ecommerceService';

export const ProductModal = ({ 
  product, 
  isOpen, 
  onClose, 
  onAddToCart,
  storeSettings = {},
  styling = {},
  className = '' 
}) => {
  const { addToCart, isInCart, getCartItem } = useCart();
  
  // State for modal content
  const [activeTab, setActiveTab] = useState('details');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [zoomed, setZoomed] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Product data state
  const [reviews, setReviews] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  
  // Form states
  const [newReview, setNewReview] = useState({
    rating: 5,
    title: '',
    comment: '',
    name: '',
    email: ''
  });

  useEffect(() => {
    if (isOpen && product) {
      loadProductData();
      resetModalState();
    }
  }, [isOpen, product]);

  const resetModalState = () => {
    setActiveTab('details');
    setSelectedImageIndex(0);
    setQuantity(1);
    setSelectedVariants({});
    setZoomed(false);
  };

  const loadProductData = async () => {
    try {
      // Load reviews
      setReviewsLoading(true);
      const productReviews = await ecommerceService.getProductReviews(product.id);
      setReviews(productReviews);
      
      // Load related products
      const related = await ecommerceService.getRelatedProducts(product.id, product.category);
      setRelatedProducts(related.slice(0, 4));
    } catch (error) {
      console.error('Error loading product data:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleAddToCart = async () => {
    setLoading(true);
    try {
      const variant = Object.keys(selectedVariants).length > 0 ? selectedVariants : null;
      await addToCart(product, quantity, variant);
      onAddToCart?.(product, quantity, variant);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVariantChange = (variantType, value) => {
    setSelectedVariants(prev => ({
      ...prev,
      [variantType]: value
    }));
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    try {
      await ecommerceService.addReview(product.id, newReview);
      await loadProductData(); // Reload reviews
      setNewReview({
        rating: 5,
        title: '',
        comment: '',
        name: '',
        email: ''
      });
      setActiveTab('reviews');
    } catch (error) {
      console.error('Error submitting review:', error);
    }
  };

  const nextImage = () => {
    if (product.images && selectedImageIndex < product.images.length - 1) {
      setSelectedImageIndex(prev => prev + 1);
    }
  };

  const prevImage = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(prev => prev - 1);
    }
  };

  const getSelectedPrice = () => {
    if (Object.keys(selectedVariants).length === 0) {
      return product.salePrice || product.price;
    }
    
    // Calculate variant price adjustments
    let basePrice = product.salePrice || product.price;
    Object.values(selectedVariants).forEach(variant => {
      if (variant.priceAdjustment) {
        basePrice += variant.priceAdjustment;
      }
    });
    
    return basePrice;
  };

  const isOutOfStock = () => {
    if (Object.keys(selectedVariants).length === 0) {
      return product.quantity === 0 || product.status === 'sold_out';
    }
    
    // Check variant stock
    const variantKey = Object.values(selectedVariants).map(v => v.id || v.name).join('-');
    const variantStock = product.variantStock?.[variantKey] || 0;
    return variantStock === 0;
  };

  const getAvailableStock = () => {
    if (Object.keys(selectedVariants).length === 0) {
      return product.quantity || 0;
    }
    
    const variantKey = Object.values(selectedVariants).map(v => v.id || v.name).join('-');
    return product.variantStock?.[variantKey] || 0;
  };

  if (!isOpen || !product) return null;

  const selectedPrice = getSelectedPrice();
  const hasDiscount = product.salePrice && product.salePrice < product.price;
  const availableStock = getAvailableStock();
  const stockStatus = isOutOfStock();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col ${className}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-900 truncate flex-1">
            {product.name}
          </h2>
          <div className="flex items-center gap-2 ml-4">
            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <Heart className="h-5 w-5" />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <Share2 className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
            {/* Left: Product Images */}
            <div className="relative bg-gray-50 flex flex-col">
              {/* Main Image */}
              <div className="flex-1 relative overflow-hidden group">
                <img
                  src={product.images?.[selectedImageIndex] || product.imageUrl}
                  alt={product.name}
                  className={`w-full h-full object-contain transition-transform duration-300 ${
                    zoomed ? 'scale-150 cursor-zoom-out' : 'cursor-zoom-in'
                  }`}
                  onClick={() => setZoomed(!zoomed)}
                />
                
                {/* Image Navigation */}
                {product.images && product.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      disabled={selectedImageIndex === 0}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={nextImage}
                      disabled={selectedImageIndex === (product.images?.length || 1) - 1}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </>
                )}

                {/* Zoom Toggle */}
                <button
                  onClick={() => setZoomed(!zoomed)}
                  className="absolute top-4 right-4 p-2 bg-white/80 hover:bg-white rounded-full shadow-sm"
                >
                  {zoomed ? <ZoomOut className="h-5 w-5" /> : <ZoomIn className="h-5 w-5" />}
                </button>

                {/* Image Counter */}
                {product.images && product.images.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                    {selectedImageIndex + 1} / {product.images.length}
                  </div>
                )}
              </div>

              {/* Image Thumbnails */}
              {product.images && product.images.length > 1 && (
                <div className="p-4 bg-white border-t">
                  <div className="flex gap-2 overflow-x-auto">
                    {product.images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImageIndex(index)}
                        className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-colors ${
                          selectedImageIndex === index
                            ? 'border-blue-500'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={image}
                          alt={`${product.name} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right: Product Details */}
            <div className="flex flex-col">
              {/* Product Info */}
              <div className="p-6 space-y-6">
                {/* Brand & Category */}
                {(product.brand || product.category) && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    {product.brand && <span className="font-medium">{product.brand}</span>}
                    {product.brand && product.category && <span>•</span>}
                    {product.category && <span className="capitalize">{product.category}</span>}
                  </div>
                )}

                {/* Product Name */}
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    {product.name}
                  </h1>
                  {product.subtitle && (
                    <p className="text-gray-600">{product.subtitle}</p>
                  )}
                </div>

                {/* Rating */}
                {product.rating && product.rating > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-5 w-5 ${
                            i < Math.floor(product.rating)
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-600">
                      {product.rating.toFixed(1)} ({product.reviewCount || 0} reviews)
                    </span>
                  </div>
                )}

                {/* Price */}
                <div className="flex items-center gap-3">
                  <span 
                    className="text-3xl font-bold"
                    style={{ color: styling.primaryColor }}
                  >
                    R{selectedPrice?.toFixed(2)}
                  </span>
                  {hasDiscount && (
                    <span className="text-xl text-gray-500 line-through">
                      R{product.price?.toFixed(2)}
                    </span>
                  )}
                  {hasDiscount && (
                    <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-sm font-medium">
                      {Math.round(((product.price - (product.salePrice || product.price)) / product.price) * 100)}% OFF
                    </span>
                  )}
                </div>

                {/* Stock Status */}
                <div className={`text-sm font-medium ${
                  stockStatus ? 'text-red-600' : 
                  availableStock <= 5 ? 'text-orange-600' : 'text-green-600'
                }`}>
                  {stockStatus ? 'Out of Stock' : 
                   availableStock <= 5 ? `Only ${availableStock} left` : 'In Stock'}
                </div>

                {/* Product Variants */}
                {product.variants && product.variants.length > 0 && (
                  <div className="space-y-4">
                    {Object.entries(
                      product.variants.reduce((acc, variant) => {
                        if (!acc[variant.type]) acc[variant.type] = [];
                        acc[variant.type].push(variant);
                        return acc;
                      }, {})
                    ).map(([variantType, variants]) => (
                      <div key={variantType}>
                        <label className="block text-sm font-medium mb-2 capitalize">
                          {variantType}:
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {variants.map((variant, index) => (
                            <button
                              key={index}
                              onClick={() => handleVariantChange(variantType, variant)}
                              className={`px-4 py-2 border rounded-lg transition-colors ${
                                selectedVariants[variantType]?.name === variant.name
                                  ? 'border-blue-500 bg-blue-50 text-blue-600'
                                  : 'border-gray-300 hover:border-gray-400'
                              }`}
                            >
                              {variant.name}
                              {variant.priceAdjustment && variant.priceAdjustment !== 0 && (
                                <span className="ml-1 text-xs">
                                  ({variant.priceAdjustment > 0 ? '+' : ''}R{variant.priceAdjustment})
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Quantity Selector */}
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium">Quantity:</label>
                  <div className="flex items-center border border-gray-300 rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      className="p-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="px-4 py-2 font-medium">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(availableStock, quantity + 1))}
                      disabled={quantity >= availableStock}
                      className="p-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <span className="text-sm text-gray-600">
                    Max: {availableStock}
                  </span>
                </div>

                {/* Add to Cart Button */}
                <button
                  onClick={handleAddToCart}
                  disabled={stockStatus || loading}
                  className={`w-full py-3 px-6 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    stockStatus
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'text-white hover:opacity-90'
                  }`}
                  style={{ 
                    backgroundColor: !stockStatus ? styling.primaryColor : undefined
                  }}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Adding...
                    </>
                  ) : stockStatus ? (
                    <>
                      <AlertCircle className="h-5 w-5" />
                      Out of Stock
                    </>
                  ) : isInCart(product.id, selectedVariants) ? (
                    <>
                      <CheckCircle className="h-5 w-5" />
                      Added to Cart
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-5 w-5" />
                      Add to Cart - R{(selectedPrice * quantity).toFixed(2)}
                    </>
                  )}
                </button>

                {/* Trust Elements */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                  {storeSettings.shipping?.freeShippingThreshold && 
                   selectedPrice >= storeSettings.shipping.freeShippingThreshold && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Truck className="h-4 w-4 text-green-600" />
                      <span>Free Shipping</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Shield className="h-4 w-4 text-blue-600" />
                    <span>Secure Payment</span>
                  </div>
                  
                  {storeSettings.returnPolicy && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="h-4 w-4 text-purple-600" />
                      <span>{storeSettings.returnPolicy}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="border-t">
                <div className="flex">
                  {[
                    { id: 'details', label: 'Details', icon: Info },
                    { id: 'reviews', label: `Reviews (${reviews.length})`, icon: Star },
                    { id: 'shipping', label: 'Shipping', icon: Truck }
                  ].map(tab => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 border-b-2 transition-colors ${
                          activeTab === tab.id
                            ? 'border-blue-500 text-blue-600 bg-blue-50'
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="text-sm font-medium">{tab.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {activeTab === 'details' && (
                    <div className="space-y-6">
                      {/* Description */}
                      {product.description && (
                        <div>
                          <h3 className="font-semibold mb-3">Description</h3>
                          <div className="prose prose-sm max-w-none">
                            <p className="text-gray-700 leading-relaxed">
                              {product.description}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Specifications */}
                      {product.specifications && (
                        <div>
                          <h3 className="font-semibold mb-3">Specifications</h3>
                          <div className="space-y-2">
                            {Object.entries(product.specifications).map(([key, value]) => (
                              <div key={key} className="flex justify-between py-2 border-b border-gray-100">
                                <span className="text-gray-600 capitalize">{key}:</span>
                                <span className="font-medium">{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Features */}
                      {product.features && product.features.length > 0 && (
                        <div>
                          <h3 className="font-semibold mb-3">Features</h3>
                          <ul className="space-y-2">
                            {product.features.map((feature, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* SKU and other details */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          {product.sku && (
                            <div>
                              <span className="text-gray-600">SKU:</span>
                              <span className="ml-2 font-medium">{product.sku}</span>
                            </div>
                          )}
                          {product.weight && (
                            <div>
                              <span className="text-gray-600">Weight:</span>
                              <span className="ml-2 font-medium">{product.weight}kg</span>
                            </div>
                          )}
                          {product.dimensions && (
                            <div>
                              <span className="text-gray-600">Dimensions:</span>
                              <span className="ml-2 font-medium">{product.dimensions}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-600">Availability:</span>
                            <span className={`ml-2 font-medium ${
                              stockStatus ? 'text-red-600' : 'text-green-600'
                            }`}>
                              {stockStatus ? 'Out of Stock' : 'In Stock'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'reviews' && (
                    <div className="space-y-6">
                      {/* Reviews Summary */}
                      {product.rating && product.rating > 0 && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center gap-4 mb-4">
                            <div className="text-center">
                              <div className="text-3xl font-bold" style={{ color: styling.primaryColor }}>
                                {product.rating.toFixed(1)}
                              </div>
                              <div className="flex justify-center mb-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`h-4 w-4 ${
                                      i < Math.floor(product.rating)
                                        ? 'text-yellow-400 fill-current'
                                        : 'text-gray-300'
                                    }`}
                                  />
                                ))}
                              </div>
                              <div className="text-sm text-gray-600">
                                {reviews.length} reviews
                              </div>
                            </div>
                            
                            <div className="flex-1 space-y-1">
                              {[5, 4, 3, 2, 1].map(rating => {
                                const count = reviews.filter(r => Math.floor(r.rating) === rating).length;
                                const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                                return (
                                  <div key={rating} className="flex items-center gap-2 text-sm">
                                    <span className="w-8">{rating}★</span>
                                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                                      <div 
                                        className="h-2 rounded-full"
                                        style={{ 
                                          width: `${percentage}%`,
                                          backgroundColor: styling.primaryColor 
                                        }}
                                      />
                                    </div>
                                    <span className="w-8 text-gray-600">{count}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Reviews List */}
                      <div className="space-y-4">
                        {reviewsLoading ? (
                          <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                          </div>
                        ) : reviews.length > 0 ? (
                          reviews.map((review, index) => (
                            <div key={index} className="border-b pb-4">
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                                  <User className="h-5 w-5 text-gray-600" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium">{review.name || 'Anonymous'}</span>
                                    <div className="flex">
                                      {[...Array(5)].map((_, i) => (
                                        <Star
                                          key={i}
                                          className={`h-4 w-4 ${
                                            i < review.rating
                                              ? 'text-yellow-400 fill-current'
                                              : 'text-gray-300'
                                          }`}
                                        />
                                      ))}
                                    </div>
                                    <span className="text-sm text-gray-500">
                                      {new Date(review.createdAt?.toDate?.() || review.createdAt).toLocaleDateString()}
                                    </span>
                                  </div>
                                  {review.title && (
                                    <h4 className="font-medium mb-2">{review.title}</h4>
                                  )}
                                  <p className="text-gray-700 mb-2">{review.comment}</p>
                                  <div className="flex items-center gap-4 text-sm text-gray-500">
                                    <button className="flex items-center gap-1 hover:text-gray-700">
                                      <ThumbsUp className="h-3 w-3" />
                                      Helpful ({review.helpful || 0})
                                    </button>
                                    <button className="flex items-center gap-1 hover:text-gray-700">
                                      <Flag className="h-3 w-3" />
                                      Report
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8">
                            <Star className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
                            <p className="text-gray-600">Be the first to review this product!</p>
                          </div>
                        )}
                      </div>

                      {/* Add Review Form */}
                      <div className="border-t pt-6">
                        <h3 className="font-semibold mb-4">Write a Review</h3>
                        <form onSubmit={handleSubmitReview} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Rating</label>
                            <div className="flex gap-1">
                              {[1, 2, 3, 4, 5].map(rating => (
                                <button
                                  key={rating}
                                  type="button"
                                  onClick={() => setNewReview(prev => ({ ...prev, rating }))}
                                  className="p-1"
                                >
                                  <Star
                                    className={`h-6 w-6 ${
                                      rating <= newReview.rating
                                        ? 'text-yellow-400 fill-current'
                                        : 'text-gray-300'
                                    }`}
                                  />
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2">Name</label>
                              <input
                                type="text"
                                value={newReview.name}
                                onChange={(e) => setNewReview(prev => ({ ...prev, name: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Email</label>
                              <input
                                type="email"
                                value={newReview.email}
                                onChange={(e) => setNewReview(prev => ({ ...prev, email: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                required
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-2">Review Title</label>
                            <input
                              type="text"
                              value={newReview.title}
                              onChange={(e) => setNewReview(prev => ({ ...prev, title: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="Give your review a title"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-2">Your Review</label>
                            <textarea
                              value={newReview.comment}
                              onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                              rows="4"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="Share your experience with this product..."
                              required
                            />
                          </div>

                          <button
                            type="submit"
                            className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
                            style={{ backgroundColor: styling.primaryColor }}
                          >
                            Submit Review
                          </button>
                        </form>
                      </div>
                    </div>
                  )}

                  {activeTab === 'shipping' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-semibold mb-4">Shipping Information</h3>
                        
                        {storeSettings.shipping?.shippingMethods && (
                          <div className="space-y-3">
                            {storeSettings.shipping.shippingMethods.map((method, index) => (
                              <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                <div>
                                  <div className="font-medium">{method.name}</div>
                                  <div className="text-sm text-gray-600">
                                    Estimated delivery: {method.estimatedDays} days
                                  </div>
                                </div>
                                <div className="font-semibold">
                                  {method.price === 0 ? 'FREE' : `R${method.price}`}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {storeSettings.shipping?.freeShippingThreshold && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                            <div className="flex items-center gap-2 text-green-800">
                              <Truck className="h-5 w-5" />
                              <span className="font-medium">
                                Free shipping on orders over R{storeSettings.shipping.freeShippingThreshold}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div>
                        <h3 className="font-semibold mb-3">Return Policy</h3>
                        <div className="text-gray-700">
                          {storeSettings.returnPolicy || "Please contact us for return information."}
                        </div>
                      </div>

                      {product.weight && (
                        <div>
                          <h3 className="font-semibold mb-3">Package Details</h3>
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-600">Weight:</span>
                                <span className="ml-2 font-medium">{product.weight}kg</span>
                              </div>
                              {product.dimensions && (
                                <div>
                                  <span className="text-gray-600">Dimensions:</span>
                                  <span className="ml-2 font-medium">{product.dimensions}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="border-t p-6 bg-gray-50">
            <h3 className="font-semibold mb-4">You might also like</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.map(relatedProduct => (
                <div
                  key={relatedProduct.id}
                  className="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    // Replace current product with related product
                    window.location.href = `#product-${relatedProduct.id}`;
                  }}
                >
                  <div className="aspect-square bg-gray-200 rounded-lg mb-2 overflow-hidden">
                    {relatedProduct.imageUrl ? (
                      <img
                        src={relatedProduct.imageUrl}
                        alt={relatedProduct.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <h4 className="font-medium text-sm mb-1 truncate">{relatedProduct.name}</h4>
                  <p className="text-sm font-semibold" style={{ color: styling.primaryColor }}>
                    R{(relatedProduct.salePrice || relatedProduct.price)?.toFixed(2)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};